import React from "react";
import { color, radius, space, text, weight, tracking } from "../tokens";

/**
 * <FreshnessTag> — "data is fresh" trust signal.
 *
 * Present on every dashboard header. Stripe / Mercury pattern.
 *
 *   status  : "live"   default → pulsing green dot + "AS OF JUST NOW"
 *             "stale"  → amber dot + custom label
 *             "paused" → gray dot + "PAUSED"
 *   label   : override the default label
 *
 * Use the freshnessLabel() helper to compute a relative-time label from
 * a Date/timestamp.
 */
const STATUS_STYLES = {
  live:   { bg: color.success.bg, fg: color.success.text, dot: color.success.solid, pulse: true },
  stale:  { bg: color.warning.bg, fg: color.warning.text, dot: color.warning.solid, pulse: false },
  paused: { bg: color.bg.subtle,  fg: color.text.tertiary, dot: color.text.tertiary, pulse: false },
};

export function FreshnessTag({ status = "live", label, style }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.live;
  const defaultLabel =
    status === "live" ? "AS OF JUST NOW" :
    status === "stale" ? "DATA STALE" : "PAUSED";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: space[2],
        padding: `3px ${space[2]}px`,
        borderRadius: radius.pill,
        background: s.bg,
        color: s.fg,
        fontSize: text.xs,
        fontWeight: weight.semibold,
        letterSpacing: tracking.wider,
        ...style,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: radius.pill,
          background: s.dot,
          animation: s.pulse ? "idz-pulse 1.4s ease-in-out infinite" : "none",
        }}
      />
      {label ?? defaultLabel}
    </span>
  );
}

/**
 * Convert a Date or timestamp (ms) to a compact relative-time label.
 * <10s → "AS OF JUST NOW"; <1m → "N SEC AGO"; <1h → "N MIN AGO"; <24h → "N HR AGO"; else absolute.
 */
export function freshnessLabel(timestamp) {
  if (!timestamp) return "—";
  const ms = timestamp instanceof Date ? timestamp.getTime() : timestamp;
  const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (seconds < 10) return "AS OF JUST NOW";
  if (seconds < 60) return `${seconds} SEC AGO`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} MIN AGO`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} HR AGO`;
  return new Date(ms).toLocaleString();
}
